import React from 'react';
import { useTenant } from '../../contexts/TenantContext';

/**
 * SchoolGuard - Protects routes that require schools to exist
 * Uses TenantContext to avoid duplicate API calls and loading states
 */
function SchoolGuard({ children, allowedPaths = [] }) {
  const { hasSchools, loading, error, getTenantUrl } = useTenant();

  // Check if current path is allowed (admin, billing, etc.)
  const currentPath = window.location.pathname;
  const isAllowedPath = allowedPaths.some(path => currentPath.includes(path));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading Dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-2">Error loading dashboard</div>
          <div className="text-gray-600 mb-4">{error}</div>
          <button 
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // If no schools and not on an allowed path, show create school message
  if (!hasSchools && !isAllowedPath) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center max-w-md">
          <div className="mb-6">
            <div className="text-6xl text-gray-300 mb-4">🏫</div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Create School to Manage Classes
            </h1>
            <p className="text-gray-600 mb-6">
              You need to create at least one school before you can manage classes, 
              take attendance, or view student data.
            </p>
          </div>
          
          <div className="space-y-3">
            <a 
              href={getTenantUrl('/admin')}
              className="block w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 font-medium transition-colors"
            >
              Go to Admin Panel
            </a>
            <p className="text-sm text-gray-500">
              Create your first school in the Admin Panel to get started
            </p>
          </div>
        </div>
      </div>
    );
  }

  // If schools exist or on allowed path, render children
  return children;
}

export default SchoolGuard;
